// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canAddAllToList,
    getCartState,
    getCurrentCartState,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

type OwnProps = {
    variant: "button" | "clickable" | "link";
    extendedStyles?: ButtonPresentationProps | ClickablePresentationProps | LinkPresentationProps;
    cartId?: string;
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    let cart;
    if (props.cartId === "current" || !props.cartId) {
        cart = getCurrentCartState(state).value;
    } else {
        cart = getCartState(state, props.cartId)?.value;
    }
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        cart,
        canAddAllToList: canAddAllToList(cart),
        isPunchOut: isPunchOutOrder(cart),
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasToasterContext &
    ResolveThunks<typeof mapDispatchToProps>;

const AddAllToList = ({
    cart,
    canAddAllToList,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    toaster,
    isPunchOut,
    extendedStyles,
    variant,
}: Props) => {
    if (!cart || isPunchOut) {
        return null;
    }

    const addAllToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfos =
            cart.cartLines
                ?.filter(o => o.productId && o.qtyOrdered)
                .map(cartLine => ({
                    productId: cartLine.productId!,
                    qtyOrdered: cartLine.qtyOrdered!,
                    unitOfMeasure: cartLine.unitOfMeasure,
                })) || [];
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toaster.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toaster.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    if (variant === "link") {
        return (
            <Link
                disabled={!canAddAllToList}
                data-test-selector="cartActionsAddAllToList"
                {...extendedStyles}
                onClick={addAllToListClickHandler}
                hoverMode={undefined}
            >
                {translate("Add All to Project List")}
            </Link>
        );
    }

    const RenderComponent = variant === "button" ? Button : Clickable;
    const styles: ButtonPresentationProps | ClickablePresentationProps | undefined = extendedStyles;
    return (
        <RenderComponent
            disabled={!canAddAllToList}
            data-test-selector="cartActionsAddAllToList"
            {...styles}
            onClick={addAllToListClickHandler}
        >
            {translate("Add All to Project List")}
        </RenderComponent>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(AddAllToList));
